/**
 * El focus de Miranda, derivat de l'URL.
 *
 * `plans/reservation-context-and-use-cases.md` §F2. **L'URL és el context**: no hi ha cap
 * `setFocus()` que una pantalla hagi de recordar de cridar (això era `setPageData`, i mai no
 * el va cridar ningú). Si `/reserves/92725627` obre la fitxa, aquesta funció ja diu que el
 * focus és aquella reserva, i el hook del xat l'envia.
 *
 * Del client al backend hi viatgen **només ids**: cap nom, cap telèfon, cap resum. Les dades
 * les carrega el servidor amb el rol de qui pregunta (`ChatFocusDto` + `agent/context/`).
 */

import { parseReservationIds, RESERVATION_PATH_PREFIX } from '@/lib/reservation-url';

export interface ReservationFocus {
  type: 'reservation';
  beds24Ids: string[];
  guestId?: string;
  conversationId?: undefined;
}

/**
 * Una conversa de l'inbox (`/whatsapp/<id>`, `plans/whatsapp-inbox.md` §5.4). La reserva de la
 * conversa la decideix el servidor, no l'URL.
 *
 * `beds24Ids` és **sempre buit** i només existeix perquè el xip del full de Miranda
 * (`MirandaSheet`) llegeix `beds24Ids[0]` i `.length` de qualsevol focus sense mirar-ne el tipus:
 * sense el camp, obrir Miranda a `/whatsapp/<id>` petaria. No s'envia mai (`toFocusPayload`).
 */
export interface WhatsappConversationFocus {
  type: 'whatsapp-conversation';
  conversationId: string;
  beds24Ids: never[];
  guestId?: undefined;
}

export type MirandaFocus = ReservationFocus | WhatsappConversationFocus;

/** Prefix de la ruta de l'inbox (`App.tsx`: `/whatsapp/:conversationId?`). */
const WHATSAPP_PATH_PREFIX = '/whatsapp';

/** Id intern (`cuid()` de Prisma), el mateix patró que valida `ChatFocusDto` al backend. */
const CONVERSATION_ID_PATTERN = /^c[a-z0-9]{20,32}$/;

/**
 * `/reserves/92725627+92725628?viatger=abc` → focus de dues reserves i un viatger.
 *
 * `null` a qualsevol altra ruta, i també quan els ids no passen la validació de
 * `parseReservationIds` (8 xifres, màxim 6): val més preguntar sense context que amb un
 * context inventat.
 *
 * `?viatger=` encara no el posa cap pantalla; es llegeix perquè el contracte del focus el
 * preveu i perquè la fitxa el podrà afegir quan s'assenyali un viatger concret.
 */
export function parseFocusFromPath(pathname: string, search = ''): MirandaFocus | null {
  const conversationFocus = parseConversationFocus(pathname);
  if (conversationFocus) return conversationFocus;

  const prefix = `${RESERVATION_PATH_PREFIX}/`;
  if (!pathname.startsWith(prefix)) return null;

  // Només el primer segment: `/reserves/92725627/qualsevol-cosa` no és una ruta nostra.
  const [segment, ...rest] = pathname.slice(prefix.length).split('/');
  if (rest.length > 0 && rest.some((part) => part !== '')) return null;

  const beds24Ids = parseReservationIds(segment);
  if (beds24Ids.length === 0) return null;

  const focus: ReservationFocus = { type: 'reservation', beds24Ids };

  const guestId = readGuestId(search);
  if (guestId) focus.guestId = guestId;

  return focus;
}

/**
 * `/whatsapp/<id>` → focus de conversa. `/whatsapp` sol (la llista) → `null`, igual que una
 * subruta desconeguda o un id que no té pinta de `cuid`.
 */
function parseConversationFocus(pathname: string): WhatsappConversationFocus | null {
  const prefix = `${WHATSAPP_PATH_PREFIX}/`;
  if (!pathname.startsWith(prefix)) return null;

  const [segment, ...rest] = pathname.slice(prefix.length).split('/');
  if (rest.some((part) => part !== '')) return null;
  if (!CONVERSATION_ID_PATTERN.test(segment)) return null;

  return { type: 'whatsapp-conversation', conversationId: segment, beds24Ids: [] };
}

/** El focus tal com viatja al backend (`ChatFocusDto`). */
export type MirandaFocusPayload =
  | { type: 'reservation'; beds24Ids: string[]; guestId?: string }
  | { type: 'whatsapp-conversation'; conversationId: string };

/**
 * El focus tal com viatja al backend (`ChatFocusDto`): només ids, i sense el `beds24Ids` buit
 * del focus de conversa (el DTO en demana almenys un si hi és).
 */
export function toFocusPayload(
  focus: MirandaFocus | null | undefined,
): MirandaFocusPayload | undefined {
  if (!focus) return undefined;
  if (focus.type === 'whatsapp-conversation') {
    return { type: focus.type, conversationId: focus.conversationId };
  }
  return focus;
}

/** `?viatger=<cuid>`, si hi és i té una pinta raonable d'id. */
function readGuestId(search: string): string | undefined {
  if (!search) return undefined;
  try {
    const value = new URLSearchParams(search).get('viatger')?.trim();
    if (!value) return undefined;
    return /^[a-zA-Z0-9_-]{1,50}$/.test(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Dos focus són el mateix? Serveix per saber quan cal reprendre el focus descartat. */
export function isSameFocus(a: MirandaFocus | null, b: MirandaFocus | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.type === b.type &&
    a.conversationId === b.conversationId &&
    a.guestId === b.guestId &&
    a.beds24Ids.length === b.beds24Ids.length &&
    a.beds24Ids.every((id, i) => id === b.beds24Ids[i])
  );
}
